import React, { useMemo, useRef, useState } from "react";
import {
  formatCurrencyNetto,
  formatDateSwiss,
  parseLocalDate,
  resolvePhaseName,
  STATUS_CONFIG,
} from "./zahlungsplanUtils";
import { validateBelegFile } from "../../services/azureBelegeService";

// Wizard, der sich öffnet, wenn eine Ausgabe im Zahlungsplan auf "Erhalten"
// gestellt wird (statusChanged=true) oder per Kontextmenü "Beleg zuordnen…"
// (statusChanged=false, ohne Statuswechsel): Beleg (PDF) hochladen oder einem
// bestehenden Beleg zuordnen, danach optional weitere Positionen aus demselben
// Zahlungsplan an den Beleg hängen (noch offene werden dabei auf "Erhalten"
// gesetzt).
const ZahlungsplanBelegDialog = ({
  entry,
  entries,
  belege,
  phases = [],
  statusChanged = true,
  onUpload,
  onLink,
  onClose,
}) => {
  const [step, setStep] = useState("ask"); // ask | upload | existing | more
  const [beleg, setBeleg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [linkedInfo, setLinkedInfo] = useState(null); // "N Positionen zugeordnet."
  const fileInputRef = useRef(null);

  // Weitere zuordenbare Positionen: Ausgaben ohne Beleg, ausser der Ausgangszahlung.
  const candidates = useMemo(() => {
    return entries
      .filter(
        (e) =>
          e.typ === "Ausgabe" &&
          e.id !== entry.id &&
          !e.beleg_id
      )
      .sort((a, b) => {
        const phaseCmp = (a.phase || "").localeCompare(b.phase || "");
        if (phaseCmp !== 0) return phaseCmp;
        const partnerCmp = (a.partner_name || "").localeCompare(b.partner_name || "");
        if (partnerCmp !== 0) return partnerCmp;
        return (a.wann || "").localeCompare(b.wann || "");
      });
  }, [entries, entry.id]);

  const entryLabel = (e) => {
    const date = parseLocalDate(e.wann || e.versand_datum);
    return [
      resolvePhaseName(e.phase, phases) || "ohne Phase",
      e.partner_name || "Sonstige",
      date ? formatDateSwiss(date) : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const handleFile = async (file) => {
    if (busy) return;
    setError(null);
    try {
      validateBelegFile(file);
    } catch (err) {
      setError(err.message);
      return;
    }
    setBusy(true);
    try {
      const uploaded = await onUpload(file);
      await onLink(uploaded, [entry]);
      setBeleg(uploaded);
      setStep("more");
    } catch (err) {
      setError(err.message || "Der Beleg konnte nicht hochgeladen werden.");
    } finally {
      setBusy(false);
    }
  };

  const handleSelectExisting = async (existing) => {
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await onLink(existing, [entry]);
      setBeleg(existing);
      setStep("more");
    } catch (err) {
      setError(err.message || "Die Zuordnung ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleLinkMore = async () => {
    if (busy || selectedIds.size === 0) return;
    setError(null);
    setBusy(true);
    try {
      const toLink = candidates.filter((e) => selectedIds.has(e.id));
      await onLink(beleg, toLink);
      setLinkedInfo(
        toLink.length === 1
          ? "1 Position zugeordnet und auf Erhalten gesetzt."
          : `${toLink.length} Positionen zugeordnet und auf Erhalten gesetzt.`
      );
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Die Zuordnung ist fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="zp-beleg-dialog-overlay" onClick={() => !busy && onClose()}>
      <div className="zp-beleg-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="zp-beleg-dialog-header">
          <h4>Beleg zuordnen</h4>
          <button
            className="zp-beleg-dialog-close"
            onClick={onClose}
            disabled={busy}
            title="Schliessen"
          >
            ×
          </button>
        </div>

        <div className="zp-beleg-dialog-body">
          <div className="zp-beleg-dialog-entry">
            {entryLabel(entry)}
            {" · "}
            {formatCurrencyNetto(entry.netto_betrag || entry.betrag || 0)}
          </div>

          {error && <div className="zp-beleg-dialog-error">{error}</div>}

          {step === "ask" && (
            <>
              <p>
                {statusChanged ? "Die Zahlung wurde auf «Erhalten» gesetzt. " : ""}
                Möchten Sie einen Beleg (PDF) hochladen oder die Zahlung einem
                bereits erfassten Beleg zuordnen?
              </p>
              <div className="zp-beleg-dialog-options">
                <button
                  className="zp-beleg-option"
                  onClick={() => setStep("upload")}
                >
                  Beleg hochladen
                </button>
                {belege.length > 0 && (
                  <button
                    className="zp-beleg-option"
                    onClick={() => setStep("existing")}
                  >
                    Bestehendem Beleg zuordnen
                  </button>
                )}
                <button className="zp-beleg-option" onClick={onClose}>
                  Nein, ohne Beleg
                </button>
              </div>
            </>
          )}

          {step === "upload" && (
            <>
              <div
                className={`zp-beleg-dropzone ${dragActive ? "drag-active" : ""} ${busy ? "busy" : ""}`}
                onClick={() => !busy && fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragActive(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) handleFile(file);
                }}
              >
                {busy
                  ? "Beleg wird hochgeladen…"
                  : "PDF hier ablegen oder klicken, um eine Datei auszuwählen"}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleFile(file);
                  e.target.value = "";
                }}
              />
              <button
                className="zp-beleg-back"
                onClick={() => setStep("ask")}
                disabled={busy}
              >
                Zurück
              </button>
            </>
          )}

          {step === "existing" && (
            <>
              <p>Wählen Sie den Beleg, zu dem diese Zahlung gehört:</p>
              <div className="zp-beleg-dialog-list">
                {belege.map((b) => {
                  const count = entries.filter((e) => e.beleg_id === b.id).length;
                  const date = parseLocalDate(b.hochgeladenAm);
                  return (
                    <button
                      key={b.id}
                      className="zp-beleg-option"
                      onClick={() => handleSelectExisting(b)}
                      disabled={busy}
                    >
                      <span className="zp-beleg-file-name">{b.fileName}</span>
                      <span className="zp-beleg-file-meta">
                        {date ? formatDateSwiss(date) : ""}
                        {count > 0
                          ? ` · ${count} Position${count === 1 ? "" : "en"}`
                          : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                className="zp-beleg-back"
                onClick={() => setStep("ask")}
                disabled={busy}
              >
                Zurück
              </button>
            </>
          )}

          {step === "more" && (
            <>
              <p>
                Beleg <strong>{beleg?.fileName}</strong> wurde der Zahlung
                zugeordnet. Sind in diesem Beleg weitere Positionen enthalten?
              </p>
              {linkedInfo && (
                <div className="zp-beleg-dialog-info">{linkedInfo}</div>
              )}
              {candidates.length === 0 ? (
                <p className="zp-beleg-dialog-empty">
                  Keine weiteren offenen Positionen vorhanden.
                </p>
              ) : (
                <div className="zp-beleg-dialog-list zp-beleg-candidates">
                  {candidates.map((e) => (
                    <label key={e.id} className="zp-beleg-candidate">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(e.id)}
                        onChange={() => toggleSelected(e.id)}
                        disabled={busy}
                      />
                      <span className="zp-beleg-candidate-label">
                        {entryLabel(e)}
                      </span>
                      <span className="zp-beleg-candidate-status">
                        {STATUS_CONFIG[e.status]?.label || e.status}
                      </span>
                      <span className="zp-beleg-candidate-amount">
                        {formatCurrencyNetto(e.netto_betrag || e.betrag || 0)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <div className="zp-beleg-dialog-actions">
                {candidates.length > 0 && (
                  <button
                    className="btn btn-primary"
                    onClick={handleLinkMore}
                    disabled={busy || selectedIds.size === 0}
                  >
                    {busy ? "Wird zugeordnet…" : "Zuordnen & auf Erhalten setzen"}
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={busy}
                >
                  Fertig
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ZahlungsplanBelegDialog;
